import { createTeoError } from './TeoError';

const API_ERROR_CODES = [
  'ApiSystemError',
  'ApiNetworkError',
  'ApiBackendError',
  'ApiHTTPError',
] as const;
type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export class ApiError<TCode extends ApiErrorCode = ApiErrorCode> extends createTeoError(
  'APIError',
  API_ERROR_CODES,
) {
  declare readonly code: TCode;
  readonly error: string;
  readonly status: number;

  protected constructor(
    code: ApiErrorCode,
    apiError: { error: string; message: string },
    status: number,
    cause?: unknown,
  ) {
    super(code, apiError.message, cause);
    this.error = apiError.error;
    this.status = status;
  }

  static getStatus(error: unknown): number | undefined {
    if (ApiError.matches(error)) {
      return (error as ApiError).status;
    }
    return 0;
  }
}

export class ApiHTTPError extends ApiError<'ApiHTTPError'> {
  readonly response: Response;

  constructor(response: Response) {
    super(
      'ApiHTTPError',
      {
        error: 'Serverfehler',
        message: `${response.statusText} [${response.status}]`,
      },
      response.status,
    );
    this.response = response;
  }
}

export class ApiNetworkError extends ApiError<'ApiNetworkError'> {
  constructor(cause: TypeError) {
    super(
      'ApiNetworkError',
      { error: 'Netzwerkfehler', message: `Server ist nicht erreichbar: ${cause.message}` },
      0,
      cause,
    );
  }
}

export class ApiBackendError extends ApiError<'ApiBackendError'> {
  constructor(apiError: { error?: string; message: string }, status: number, cause?: unknown) {
    super(
      'ApiBackendError',
      { error: apiError.error || 'Serverfehler', message: apiError.message },
      status,
      cause,
    );
  }
}

export class ApiSystemError extends ApiError<'ApiSystemError'> {
  constructor(cause: unknown) {
    const message = cause instanceof Error ? cause.message : String(cause);
    super('ApiSystemError', { error: 'Systemfehler', message }, -1, cause);
  }
}
